// Your Service Record, kept in this browser (like Godot's per-player Profile): counts the run
// from the reliable events every client already receives, files commendations, and remembers
// the look you wear. No new RPCs for counting: the server broadcasts every event to every
// cowboy, so a posse milestone (work order, island, boss, extraction) lands on everyone's record
// at once and the posse climbs together. Personal stats (detonations, revives, kills) only count
// your own pid.
//
// Wiring: GameClient feeds onEvent(). The run loop's events (sim/run.ts: Island, Run, RunEnd, and
// the boss's Dead) drive the posse counts.

import { Ev, GameEvent } from '../../sim/codec';
import { BossEv } from '../../sim/boss';
import { RunPhase } from '../../sim/run';
import { BONUS_INDEX } from '../../sim/world';
import {
  Commendation, Finish, Ledger, Stat, blankLedger, contractsMask, earnedCount, openLedger, rankOf, sanitizeLedger, settle,
  titlesOpen, wornFinish, wornTitle,
} from '../../sim/progression';

const KEY = 'emg.ledger';
/** the design review's achievements (PR #47) live here; their names become wearable titles */
const PROFILE_KEY = 'emg.profile';

/** `?record=open` (or the Service Record's "open the whole rack" dev toggle) shows everything earned. */
function openMode(): boolean {
  try {
    if (new URLSearchParams(location.search).get('record') === 'open') return true;
    return localStorage.getItem('emg.record.open') === '1';
  } catch { return false; }
}

export function loadLedger(): Ledger {
  if (openMode()) {
    const l = openLedger();
    try { l.look = sanitizeLedger(JSON.parse(localStorage.getItem(KEY) ?? 'null')).look; } catch { /* ignore */ }
    return l;
  }
  try { return sanitizeLedger(JSON.parse(localStorage.getItem(KEY) ?? 'null')); } catch { return blankLedger(); }
}

export function saveLedger(l: Ledger) {
  if (openMode()) { saveLook(l); return; } // never write the open rack over a real record
  try { localStorage.setItem(KEY, JSON.stringify(l)); } catch { /* private mode: lives for this session only */ }
}

function saveLook(l: Ledger) {
  try {
    const real = sanitizeLedger(JSON.parse(localStorage.getItem(KEY) ?? 'null'));
    real.look = l.look;
    localStorage.setItem(KEY, JSON.stringify(real));
  } catch { /* ignore */ }
}

/** Achievement names from the design review's profile, if this browser has one. */
export function achievementTitles(): string[] {
  try {
    const p = JSON.parse(localStorage.getItem(PROFILE_KEY) ?? 'null') as { ach?: Record<string, number> } | null;
    if (!p?.ach) return [];
    const names: Record<string, string> = {
      first_det: 'Accelerated Depreciation', team_det: 'Shared Custody', assist5: 'Middle Management', chain5: 'Chain of Custody',
      multi5: 'Everything Must Go', pyrrhic: 'Pyrrhic Cowboy', metal: 'Scrap Value', heat: 'Beat the Heat', revive10: 'Next of Kin',
      eom: 'Employee of the Month', ping: 'Hand Signals',
    };
    return Object.keys(p.ach).filter((k) => p.ach![k] && names[k]).map((k) => names[k]);
  } catch { return []; }
}

export interface LookWire { finish: number; contracts: number; title: string }

export class Tracker {
  ledger = loadLedger();
  /** new commendations, for the toast */
  onEarned: (c: Commendation) => void = () => {};
  /** the look changed (title promotion, a new finish picked): resend it to the posse */
  onLook: (w: LookWire) => void = () => {};
  private dirty = false;
  private savedAt = 0;

  /** the look as it goes on the wire */
  wire(): LookWire {
    const l = this.ledger;
    return { finish: wornFinish(l), contracts: contractsMask(l), title: wornTitle(l, achievementTitles()) };
  }

  bump(stat: Stat, n = 1) {
    this.ledger.stats[stat] += n;
    this.dirty = true;
    const before = earnedCount(this.ledger);
    const fresh = settle(this.ledger);
    for (const c of fresh) this.onEarned(c);
    if (fresh.length) {
      // a rank-up changes the worn title for anyone wearing their rank; finishes and contracts change the wire too
      if (rankOf(before) !== rankOf(earnedCount(this.ledger)) || fresh.some((c) => c.reward.kind !== 'title')) this.onLook(this.wire());
      this.flush(true);
    }
  }

  setLook(finish: Finish, title: string) {
    this.ledger.look = { finish, title };
    this.flush(true);
    this.onLook(this.wire());
  }

  titles() { return titlesOpen(this.ledger, achievementTitles()); }

  /** on the bonus gauntlet right now (its waves count as work orders; the island itself doesn't) */
  private onBonus = false;

  /** feed every server event (myId = this client's pid) */
  onEvent(e: GameEvent, myId: number) {
    const f = e.f;
    switch (e.t) {
      case Ev.Island: {
        // [index, kind, village, island in village]: the first island of village 1 is a new shift
        this.onBonus = f[0] >= BONUS_INDEX;
        if (!this.onBonus && f[2] === 0 && f[3] === 0) this.bump('runs');
        break;
      }
      case Ev.Run: {
        // [phase, timer, wave, waves]: wave N+1 composing means wave N was filled; the doors opening
        // means the last one was. The gauntlet running out of clock also opens the doors, so the
        // bonus island never counts as a cleared island (only its filled waves do).
        const [phase, , wave] = f;
        if (phase === RunPhase.Waves && wave > 1) this.bump('orders');
        else if (phase === RunPhase.GateOpen && !this.onBonus) { this.bump('orders'); this.bump('islands'); }
        break;
      }
      case Ev.Boss: if (f[1] === BossEv.Dead) { this.bump('bosses'); this.bump('islands'); } break;
      case Ev.RunEnd: if (f[0] === 0) this.bump('extracts'); break;
      case Ev.EnemyDie: if (f[5] === myId) this.bump('kills'); break;
      case Ev.Detonate: if (f[1] === myId) this.bump('dets'); break;
      case Ev.Revive: if (f[1] === myId) this.bump('revives'); break;
    }
  }

  /** write at most every 3 s while fighting (kills and detonations tick fast) */
  flush(force = false) {
    if (!this.dirty && !force) return;
    const now = performance.now();
    if (!force && now - this.savedAt < 3000) return;
    saveLedger(this.ledger);
    this.dirty = false; this.savedAt = now;
  }
}
